import cloneDeep from 'lodash/cloneDeep';
import {
  WIRInterventionCloseOutSections,
  WIRPhaseSectionApiKeys,
  WIRStageGate5Sections,
} from 'pages/WellGuardian/WIR/wir.constants';
import { useCallback, useMemo } from 'react';
import { useCountryState, useSelectedWIR } from 'redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import {
  WIR,
  WIREquipmentIdentification,
  WIRInitialRequestInformation,
  WIRManagerialSummary,
  WIROrganiseKickOffMeeting,
  WIRSection,
  WIRSectionDTO,
  WIRWellInformation,
} from 'types/wellGuardian';

import {
  getInterventionCloseoutChecklists,
  getInterventionCloseOutPhaseName,
  getInterventionCloseOutSectionKey,
} from '../utils/getSectionData';

const getIsTicked = (
  subphase:
    | WIRSection
    | WIRInitialRequestInformation
    | WIROrganiseKickOffMeeting
    | WIRWellInformation
    | WIREquipmentIdentification
    | WIRManagerialSummary
    | undefined,
) => {
  if (subphase?.isRequired) {
    return subphase?.attachments && subphase?.attachments?.length > 0;
  } else {
    return subphase?.comments && subphase?.comments?.length > 0;
  }
};

export const useCloseOutChecklistData = () => {
  const wirFromState = useSelectedWIR();
  const wir = useMemo(() => cloneDeep(wirFromState), [wirFromState]);
  const countryState = useCountryState();
  const phase = getInterventionCloseOutSectionKey(countryState);

  const wirSections = useMemo(() => wir?.phases?.[phase]?.sections, [phase, wir?.phases]);

  const wircloseChecklist = useMemo(
    () => wir?.phases?.[phase]?.sections?.closeOutChecklists,
    [phase, wir?.phases],
  );

  const getChecklistData = useCallback(() => {
    const checklistObj: WIRSectionDTO[] = [];
    for (const sec in wirSections) {
      if (getInterventionCloseoutChecklists(countryState).includes(sec)) {
        const subphase = wirSections[sec as keyof typeof wirSections];

        const isTicked = getIsTicked(subphase);

        checklistObj.push({
          ...subphase,
          section: WIRPhaseSectionApiKeys[sec as keyof typeof WIRPhaseSectionApiKeys],
          phase: getInterventionCloseOutPhaseName(countryState),
          isTicked: isTicked,
          sectionCompleteConfirmation: isTicked,
        });
      }
    }

    return checklistObj;
  }, [countryState, wirSections]);

  const closeOutChecklistSectionPayload = useMemo(() => {
    const CHECKLIST_DATA = getChecklistData();

    const isTickedAll = CHECKLIST_DATA.find(checklist => !checklist.isTicked);

    const wirChecklist: WIRSectionDTO = {
      ...wircloseChecklist,
      section: 'CLOSE_OUT_CHECKLISTS',
      phase: getInterventionCloseOutPhaseName(countryState),
      isTicked: isTickedAll === undefined,
    };
    CHECKLIST_DATA.push(wirChecklist);

    return [...CHECKLIST_DATA];
  }, [countryState, getChecklistData, wircloseChecklist]);

  const saveCloseoutChecklistSection = useCallback(
    async (section: string, processSaveResponse: (wirResponse: WIR | null) => void) => {
      const phaseName = getInterventionCloseOutPhaseName(countryState);
      if (
        wirFromState?.wirNumber &&
        (section === WIRInterventionCloseOutSections.closeOutChecklists ||
          section === WIRStageGate5Sections.closeOutChecklists)
      ) {
        processSaveResponse(
          await wellGuardianApiClient.updateWIRCloseoutChecklistSection(
            wirFromState?.wirNumber,
            closeOutChecklistSectionPayload,
            phaseName,
            countryState,
          ),
        );
      }
    },
    [wirFromState, closeOutChecklistSectionPayload, countryState],
  );

  return {
    saveCloseoutChecklistSection,
    closeOutChecklistSectionPayload,
  };
};
